export const ProtocolIntelligenceStatusTypes = {
  PENDING: 'PENDING',
  COMPLETED: 'COMPLETED',
} as const;

export type ProtocolIntelligenceStatus =
  (typeof ProtocolIntelligenceStatusTypes)[keyof typeof ProtocolIntelligenceStatusTypes];

export type ProtocolIntelligenceApprovalResponse = {
  protocolId: string;
  status: ProtocolIntelligenceStatus;
  approvedBy: string;
  approvedAt: string;
};

export type IssueCode = {
  id?: string;
  code: string;
  question: string;
  description?: string;
};

export type JobType = 'ISSUECODE' | 'RELEVANCY';
export type JobStatus =
  | 'Draft'
  | 'PENDING'
  | 'PROCESSING'
  | 'COMPLETED'
  | 'SUBMITTED'
  | 'FAILED';

export interface IAIReviewJobGroup {
  jobGroupId: number;
  name: string;
  description: string;
  jobCount: number;
  createdAt: string;
  createdBy: string;
}

export interface IAIReviewJobGroupsResponse {
  jobGroups: IAIReviewJobGroup[];
  totalCount: number;
  totalJobGroupCount: number;
}

export interface ICreateAIReviewJobGroupPayload {
  name: string;
}

export interface ICreateAIReviewJobGroupResponse {
  jobGroupId: number;
  errorMessage: string;
}

export interface IUpdateAIReviewJobGroupPayload {
  jobGroupId: number;
  name: string;
}

export interface IUpdateAIReviewJobGroupResponse {
  jobGroupId: number;
  errorMessage: string;
}

export interface IDeleteAIReviewJobGroupPayload {
  jobGroupId: number;
}
export interface IDeleteAIReviewJobGroupResponse {
  jobGroupId: number;
  errorMessage: string;
}

export interface IDeleteAIReviewJobPayload {
  jobId: string | number;
}

export interface IDeleteAIReviewJobResponse {
  jobId: string | number;
}

export interface IRetryAIReviewJobPayload {
  jobId: string | number;
}

export interface IRetryAIReviewJobResponse {
  jobId: string | number;
  status: 'RETRY_QUEUE';
  message: string;
  totalDocs: number;
  retryCount: number;
}

export interface IAddJobToGroupPayload {
  groupId: string;
  jobId: string;
  addedBy?: string;
  reason?: string;
}

export interface IAddJobToGroupResponse {
  groupId: string;
  jobId: string;
  status: 'ADDED';
  addedAt: string;
  addedBy?: string;
}

export interface IRemoveJobFromGroupPayload {
  groupId: string;
  jobId: string;
}

export interface IRemoveJobFromGroupResponse {
  groupId: string;
  jobId: string;
  status: 'REMOVED';
  removedAt: string;
  removedBy?: string;
}

export interface IAIReviewProtocolIntelligencePayload {
  protocolId: string;
}

export interface ICommonProtocolIntelligenceResponse {
  protocolId: string;
  caseContext: string;
  relevanceCriteria: string;
  issueCodes: IssueCode[];
  isActive: boolean;
  lastModifiedAt: string;
}
export interface IUpdateProtocolIntelligenceDetailsPayload {
  // protocolId is for param but not the part of payload
  protocolId: string;
  caseContext: string;
  relevanceCriteria: string;
  issueCodes: IssueCode[];
  isActive: boolean;
}

export interface IUpdateProtocolIntelligenceDetailsResponse
  extends ICommonProtocolIntelligenceResponse {
  lastModifiedBy: string;
}

export interface IAIReviewProtocolIntelligenceResponse
  extends IUpdateProtocolIntelligenceDetailsPayload {
  fileName: string;
  uploadedAt: string;
  isApproved: boolean;
  lastModifiedAt: string;
  lastModifiedByUserId: string;
  aIstatus: ProtocolIntelligenceStatus;
}

export interface AIReviewCreateProtocolIntelligencePayload {
  file: File | null;
}

export interface IGetAIReviewSourceTypeResponse {
  id: number;
  sourceType: string;
}

export type CreateIssueCode = {
  code: string;
  question: string;
};
export interface ICreateAIReviewJobPayload {
  jobType: JobType;
  jobName: string;
  description: string;
  groupID: number; // -1 by default we need to pass if not selected any
  caseContext: string;
  issueCodes: CreateIssueCode[];
  groundTruth: string;
  relevanceCriteria: string;
  sourceType: string;
  sourceID: number; // 0 by default we need to pass if not selected any
}

export interface IUpdateAIReviewJobPayload {
  jobId: number;
  jobType: JobType;
  jobName: string;
  description: string;
  groupID: number; // -1 by default we need to pass if not selected any
  caseContext: string;
  issueCodes: CreateIssueCode[];
  groundTruth: string;
  relevanceCriteria: string;
  sourceType: string;
  sourceID: number; // 0 by default we need to pass if not selected any
}
export interface ICreateAIReviewJobResponse {
  jobId: number; // 0 if not created
  errorMessage: string; // empty if no error
}

export interface IUpdateAIReviewJobResponse {
  jobId: number; // 0 if not created
  errorMessage: string; // empty if no error
}

export interface ISubmitAIReviewJobPayload {
  jobId: number;
}

export interface ISubmitAIReviewJobResponse {
  jobId: number;
  jobName: string;
  jobType: JobType;
  status: JobStatus;
  createdAt: string; // ISO timestamp
}

export interface IGetAIReviewJobDetailsPayload {
  jobId: number | string;
}

export interface JobDetailsIssueCode {
  code: string;
  question: string;
}

export type AIReviewJobType = 'ISSUECODE' | 'RELEVANCY';

export interface IGetAIReviewJobDetailsResponse {
  jobId: number;
  jobName: string;
  jobType: AIReviewJobType;
  firstName: string;
  lastName: string;
  description: string;
  createdBy: string;
  createdAt: string;
  status: JobStatus;
  sourceID: number;
  sourceType: string;
  sourceName: string;
  caseContext: string;
  issueCodes: IssueCode[];
  groundTruth: string;
  relevanceCriteria: string;
  groupID: string;
  groupName: string;
}
export interface IAIReviewEditJobNamePayload {
  jobId: string;
  name: string;
}

export interface IAIReviewEditJobNameResponse {
  jobId: string;
  name: string;
  updatedBy: string;
  updatedAt: string;
}

export interface IAIReviewJobsExportRequest {
  filters?: {
    jobName?: string;
    status?: string;
    createdDateFrom?: string;
    createdDateTo?: string;
  };
  sort?: {
    field: string;
    direction: 'ASC' | 'DESC';
  };
  pagination?: {
    page: number;
    pageSize: number;
  };
}

export interface IExportAIReviewJobPdfPayload {
  jobId: string | number;
}
export interface IGetAIReviewJobResultPayload {
  jobId: string | number;
}
export interface IGetAIReviewJobResultResponse {
  jobId: string | number;
  totalDocCount: number;
  excludedCount: number;
  submittedCount: number;
  completedCount: number;
  erroredCount: number;
  relevantDocCount: number;
  nonRelevantDocCount: number;
  lastUpdated: string;
}

export interface IGetAIReviewDocErrorPanelPayload {
  page: number;
  pageSize: number;
  search?: string;
  sortBy: string;
  sortOrder: string;
}

export interface IAIReviewDocErrorPanel {
  docId: string;
  errorMessage: string;
}

export interface IAIReviewDocErrorPanelResponse {
  items: IAIReviewDocErrorPanel[];
  total: number;
}

export interface IGetDownloadIssueCodingResultsResponse {
  blob: Blob;
  filename: string;
  contentDisposition?: string;
}
export interface IAIReviewJobResultColumnFilter {
  columnName: string;
  columnValue: string;
}

export interface IAIReviewJobResultIssueCodeListPayload {
  sortingColumn: string;
  direction: string;
  columnFilter: IAIReviewJobResultColumnFilter[];
}
export interface IAIReviewissueCodeResults {
  issueCode: string;
  question: string;
  relevantDocCount: number;
  nonRelevantDocCount: number;
  documentId: number;
}
export interface IAIReviewJobResultIssueCodeListResponse {
  jobId: string;
  issueCodeResults: IAIReviewissueCodeResults[];
}

export interface IAIReviewJobResultRelevancyListPayload {
  sortingColumn: string;
  direction: string;
  displayLength: number;
  pageNumber: number;
  columnFilter: IAIReviewJobResultColumnFilter[];
}
export interface IAIReviewRelevancyResults {
  documentId: string;
  relevancy: string;
  groundTruthIndicator: boolean;
  groundTruthRelevancy: string;
  confidenceScore: number;
  reason: string;
  excerpt: string;
  dateProcessed: string;
}
export interface IAIReviewJobResultRelevancyListResponse {
  jobId: string;
  documents: IAIReviewRelevancyResults[];
}
interface IColumnFilter {
  columnName: string;
  value: string | number;
}
export interface IGetJobListPayload {
  sortingColumn: string;
  direction: string;
  displayLength: number; // keep -1 for all the rows
  pageNumber: number;
  columnFilter: IColumnFilter[];
}

export interface IJobs {
  jobId: number;
  jobName: string;
  jobGroupId: number | null;
  jobGroupName: string | null;
  jobType: string;
  createdDate: string;
  executionDate: string | null;
  createdBy: string;
  source: string;
  totalDocCount: number | null;
  completedDocCount: number | null;
  excludedErrorDocCount: number | null;
  status: string | null;
}
export interface IGetJobListResponse {
  jobs: IJobs[];
  totalCount: number;
}

export interface IConfusionMatrix {
  truePositives: number;
  falsePositives: number;
  trueNegatives: number;
  falseNegatives: number;
}

export interface IMetrics {
  accuracy: number;
  precision: number;
  recall: number;
  f1Score: number;
}

export interface IGetGroundTruthEvaluationResponse {
  jobId: string;
  groundTruthName: string;
  groundTruthDocCount: number;
  confusionMatrix: IConfusionMatrix;
  metrics: IMetrics;
}

export interface IDeleteAIProtocolIntelligencePayload {
  protocolId: string;
}

export interface IDeleteAIProtocolIntelligenceResponse {
  protocolId: string;
}
